/**
 * CauseReportPage - Main page component
 */

import { useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { ErrorBoundary } from '../../../ui/error-boundary';
import { Timestamp } from '../../../ui/timestamp';
import { FilterStrip, type ConductDimension } from '../../records/filter';
import { useReportFilterState } from '../useReportFilterState';
import { useOrganizationDisplaySettings } from '../../../../hooks/useOrganizationDisplaySettings';
import { useCollection } from '../../../../hooks/useRecords';
import { useNavigation } from '../../../../contexts/NavigationContext';
import {
  useCauseReport,
  useTopCauses,
  useOutcomes,
  useDimensionCooccurrence,
  useHighRiskRecords,
} from './hooks';
import {
  OverviewPanel,
  CategoryTreemap,
  TopCausesPanel,
  OutcomesGrid,
  DimensionCooccurrence,
  HighRiskRecords,
  ExportButton,
  CauseRecordsDialog,
  DimensionCauseRecordsDialog,
} from './components';
import type { ConductDimensionId } from './types';
import { TREEMAP_COLORS } from './constants';
import { RootCauseHistoricalBadge, RootCauseStatusBanner } from '../../common/components';

function CauseReportPageInner() {
  const { datasetId } = useParams<{ datasetId: string }>();
  const { navigateWithReferrer } = useNavigation();
  const { filters, setFilters, reset } = useReportFilterState(datasetId ?? '');
  const { data: organizationDisplaySettings } = useOrganizationDisplaySettings();
  const rootCauseClassificationEnabled =
    organizationDisplaySettings?.root_cause_classification_enabled !== false;
  const { data: collectionData } = useCollection(datasetId ?? '');

  // Category selection state
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();

  // Dimension cause records dialog state
  const [dimensionCauseDialogState, setDimensionCauseDialogState] = useState<{
    isOpen: boolean;
    dimensionId: ConductDimensionId | null;
    dimensionName: string;
    causeId: string | null;
    causeName: string;
  }>({
    isOpen: false,
    dimensionId: null,
    dimensionName: '',
    causeId: null,
    causeName: '',
  });

  // Cause records dialog state (for Top 10 Root Causes)
  const [causeDialogState, setCauseDialogState] = useState<{
    isOpen: boolean;
    causeId: string | null;
    causeName: string;
  }>({
    isOpen: false,
    causeId: null,
    causeName: '',
  });

  // Foundation data
  const {
    overview,
    categories,
    isLoading: foundationLoading,
    isError,
  } = useCauseReport(datasetId ?? '', filters);

  // Top causes data
  const {
    data: topCauses,
    isLoading: topCausesLoading,
    isError: isTopCausesError,
  } = useTopCauses(datasetId ?? '', filters, selectedCategory);

  // Outcomes data
  const {
    data: outcomes,
    isLoading: outcomesLoading,
    isError: isOutcomesError,
  } = useOutcomes(datasetId ?? '', filters);

  // Dimensions data
  const {
    data: dimensions,
    isLoading: dimensionsLoading,
    isError: isDimensionsError,
  } = useDimensionCooccurrence(datasetId ?? '', filters);

  // High-risk records data
  const {
    data: highRisk,
    isLoading: highRiskLoading,
    isError: isHighRiskError,
  } = useHighRiskRecords(datasetId ?? '', filters);

  // Handle "View Records" click - open cause records dialog
  const handleViewRecords = useCallback((labelId: string, labelName: string) => {
    setCauseDialogState({
      isOpen: true,
      causeId: labelId,
      causeName: labelName,
    });
  }, []);

  // Handle closing the cause records dialog
  const handleCloseCauseDialog = useCallback(() => {
    setCauseDialogState({
      isOpen: false,
      causeId: null,
      causeName: '',
    });
  }, []);

  // Handle dimension cause click - open dialog
  const handleDimensionCauseClick = useCallback(
    (dimensionId: ConductDimensionId, causeId: string, causeName: string) => {
      // Get dimension display name from data
      const dimension = dimensions?.dimensions.find((d) => d.dimension === dimensionId);
      setDimensionCauseDialogState({
        isOpen: true,
        dimensionId,
        dimensionName: dimension?.displayName || dimensionId,
        causeId,
        causeName,
      });
    },
    [dimensions]
  );

  // Handle closing the dimension cause dialog
  const handleCloseDimensionCauseDialog = useCallback(() => {
    setDimensionCauseDialogState({
      isOpen: false,
      dimensionId: null,
      dimensionName: '',
      causeId: null,
      causeName: '',
    });
  }, []);

  // Compute active dimensions from filters (convert back to Cause Report dimension IDs).
  // TCF can filter records, but this report chart only supports the three conduct experience dimensions.
  const activeDimensions = useMemo<ConductDimensionId[]>(() => {
    const reverseMap: Partial<Record<ConductDimension, ConductDimensionId>> = {
      harm: 'foreseeable_harm',
      strain: 'customer_strain',
      dissatisfaction: 'dissatisfaction',
    };
    return (filters.dimensions?.values || [])
      .map((d) => reverseMap[d])
      .filter((dimension): dimension is ConductDimensionId => Boolean(dimension));
  }, [filters.dimensions?.values]);

  // Handle dimension column click - toggle dimension filter
  const handleDimensionClick = useCallback(
    (dimension: ConductDimensionId) => {
      // Map cause report dimension ID to filter ConductDimension type
      const dimensionMap: Record<ConductDimensionId, ConductDimension> = {
        foreseeable_harm: 'harm',
        customer_strain: 'strain',
        dissatisfaction: 'dissatisfaction',
      };

      const filterDimension = dimensionMap[dimension];
      const currentDimensions = filters.dimensions?.values || [];

      // Toggle: add if not present, remove if present
      const newDimensions = currentDimensions.includes(filterDimension)
        ? currentDimensions.filter((d) => d !== filterDimension)
        : [...currentDimensions, filterDimension];

      setFilters({
        ...filters,
        dimensions: newDimensions.length > 0
          ? { values: newDimensions, logic: filters.dimensions?.logic || 'or' }
          : undefined,
      });
    },
    [filters, setFilters]
  );

  // Handle high-risk record click — navigate in-app, carrying a referrer so
  // "Back" on the record returns to this report.
  const handleRecordClick = useCallback(
    (recordId: string) => {
      navigateWithReferrer(`/v4/records/${datasetId}/${recordId}`);
    },
    [datasetId, navigateWithReferrer]
  );

  const isLoading = foundationLoading;

  if (!datasetId) {
    return <div>Dataset ID is required</div>;
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-[var(--go-spring)]">
      <div className="flex-1 min-h-0 overflow-auto px-4 py-4 md:px-6 md:py-5 xl:px-8 xl:py-6">
        <div className="flex w-full flex-col gap-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link to="/v4/records" className="text-primary font-medium hover:underline">Monitor</Link>
            <span>/</span>
            <Link to={`/v4/records/${datasetId}`} className="text-primary font-medium hover:underline">{collectionData?.collection?.name ?? 'Collection'}</Link>
            <span>/</span>
            <span>Cause Report</span>
          </div>
          <section className="flex flex-col gap-4 rounded-[28px] border border-border/70 bg-background/90 px-6 py-5 shadow-[var(--shadow-card)] backdrop-blur-sm lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Cause Report
              </p>
              {!rootCauseClassificationEnabled && (
                <div className="mt-3">
                  <RootCauseHistoricalBadge showLabel />
                </div>
              )}
              <h1 className="mt-2 font-jakarta text-3xl font-extrabold tracking-[-0.04em] text-foreground">
                Why customers experience dissatisfaction, harm, and strain
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Understand the drivers behind root causes, shape investigation priorities, and drill through to records without leaving the report.
              </p>
              {overview?.lastUpdated && (
                <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  Last updated <Timestamp value={overview.lastUpdated} format="absolute" />
                </p>
              )}
            </div>
            <ExportButton
              datasetId={datasetId}
              filters={filters}
              disabled={isLoading}
            />
          </section>

          <RootCauseStatusBanner settings={organizationDisplaySettings} />

          <FilterStrip
            filters={filters}
            onFiltersChange={setFilters}
            onReset={reset}
            isLoading={isLoading}
            tcfAvailable={collectionData?.hasTcfData}
            className="rounded-[24px] border border-border/70 bg-background/90 p-4 shadow-[var(--shadow-card)] backdrop-blur-sm"
          />

          <OverviewPanel
            data={overview}
            isLoading={foundationLoading}
            isError={isError}
          />

          <CategoryTreemap
            data={categories}
            selectedCategory={selectedCategory}
            onCategorySelect={setSelectedCategory}
            isLoading={foundationLoading}
            isError={isError}
          />

          <TopCausesPanel
            data={topCauses}
            categoryFilter={
              selectedCategory
                ? categories?.categories.find(c => c.id === selectedCategory)?.name
                : undefined
            }
            barColor={
              selectedCategory
                ? TREEMAP_COLORS[
                    (categories?.categories.findIndex(c => c.id === selectedCategory) ?? 0) %
                      TREEMAP_COLORS.length
                  ]
                : undefined
            }
            onViewRecords={handleViewRecords}
            isLoading={topCausesLoading}
            isError={isTopCausesError}
          />

          <DimensionCooccurrence
            data={dimensions}
            onDimensionClick={handleDimensionClick}
            onCauseClick={handleDimensionCauseClick}
            activeDimensions={activeDimensions}
            isLoading={dimensionsLoading}
            isError={isDimensionsError}
          />

          <OutcomesGrid
            data={outcomes}
            onCauseClick={handleViewRecords}
            isLoading={outcomesLoading}
            isError={isOutcomesError}
          />

          <HighRiskRecords
            data={highRisk}
            onRecordClick={handleRecordClick}
            onCauseClick={handleViewRecords}
            isLoading={highRiskLoading}
            isError={isHighRiskError}
          />

          {/* Dialogs - portaled to document.body, zero layout impact */}
          <CauseRecordsDialog
            isOpen={causeDialogState.isOpen}
            onClose={handleCloseCauseDialog}
            causeId={causeDialogState.causeId}
            causeName={causeDialogState.causeName}
            datasetId={datasetId}
            filters={filters}
            onCauseClick={handleViewRecords}
          />
          <DimensionCauseRecordsDialog
            isOpen={dimensionCauseDialogState.isOpen}
            onClose={handleCloseDimensionCauseDialog}
            dimensionId={dimensionCauseDialogState.dimensionId}
            dimensionName={dimensionCauseDialogState.dimensionName}
            causeId={dimensionCauseDialogState.causeId}
            causeName={dimensionCauseDialogState.causeName}
            datasetId={datasetId}
            filters={filters}
            onCauseClick={handleViewRecords}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * CauseReportPage wrapped with ErrorBoundary for graceful error recovery
 */
export function CauseReportPage() {
  return (
    <ErrorBoundary>
      <CauseReportPageInner />
    </ErrorBoundary>
  );
}
